import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    FormControlLabel,
    IconButton,
    InputAdornment,
    InputLabel,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Paper,
    Select,
    Skeleton,
    Snackbar,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import RemoveIcon from '@mui/icons-material/Remove';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { planService } from '../../services/api';
import AuthContext from '../../context/AuthContext';
import AppLayout from '../layout/AppLayout';
import { INK, MUTED, RULE, formatCount } from '../analytics/analyticsUi';

const GREEN = '#1BAA52';
const DANGER = '#C2410C';

const FEATURES = [
    { key: 'api', label: 'API access', short: 'API' },
    { key: 'sharedInbox', label: 'Shared inbox', short: 'Shared inbox' },
    { key: 'sentimentAnalysis', label: 'Sentiment analysis', short: 'Sentiment' },
    { key: 'bulkMessaging', label: 'Bulk messaging', short: 'Bulk' }
];

const CURRENCIES = [
    { value: 'INR', label: 'Indian rupee (INR)', symbol: '₹' },
    { value: 'USD', label: 'US dollar (USD)', symbol: '$' },
    { value: 'EUR', label: 'Euro (EUR)', symbol: '€' }
];

const emptyForm = {
    name: '',
    price: '0',
    currency: 'INR',
    messageRateLimit: '0',
    features: {
        api: false,
        sharedInbox: false,
        sentimentAnalysis: false,
        bulkMessaging: false
    },
    maxUsers: '1',
    fileStorageLimit: '1',
    isActive: true,
    description: ''
};

const formatPrice = (amount, currency) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: currency || 'INR', maximumFractionDigits: 2 })
        .format(amount || 0)
        .replace(/\.00$/, '');

const isWholeNumber = (value) => /^\d+$/.test(String(value).trim());

const validate = (form) => {
    const errors = {};
    const price = Number(form.price);
    if (!form.name.trim()) errors.name = 'Enter a plan name';
    if (String(form.price).trim() === '' || Number.isNaN(price) || price < 0) errors.price = 'Enter a price of 0 or more';
    if (!isWholeNumber(form.messageRateLimit) || Number(form.messageRateLimit) <= 0) errors.messageRateLimit = 'Enter a whole number above 0';
    if (!isWholeNumber(form.maxUsers) || Number(form.maxUsers) < 1) errors.maxUsers = 'Allow at least 1 user';
    const storage = Number(form.fileStorageLimit);
    if (String(form.fileStorageLimit).trim() === '' || Number.isNaN(storage) || storage <= 0) errors.fileStorageLimit = 'Enter a storage limit above 0';
    return errors;
};

// ---------------------------------------------------------------------------
// Small pieces
// ---------------------------------------------------------------------------
const FeatureMark = ({ enabled, label }) => (
    enabled
        ? <CheckIcon aria-label={`${label} included`} sx={{ fontSize: 18, color: GREEN, verticalAlign: 'middle' }} />
        : <RemoveIcon aria-label={`${label} not included`} sx={{ fontSize: 18, color: '#B8BFCB', verticalAlign: 'middle' }} />
);

const StatusDot = ({ active }) => (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, whiteSpace: 'nowrap' }}>
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: active ? GREEN : '#B8BFCB' }} />
        <Typography variant="body2" sx={{ color: INK }}>{active ? 'Active' : 'Inactive'}</Typography>
    </Box>
);

const RowActions = ({ plan, onEdit, onDelete }) => {
    const [anchor, setAnchor] = useState(null);
    const close = () => setAnchor(null);

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
            <Button size="small" onClick={() => onEdit(plan)} sx={{ textTransform: 'none', fontWeight: 600 }}>
                Edit
            </Button>
            <IconButton
                size="small"
                onClick={(e) => setAnchor(e.currentTarget)}
                aria-label={`More actions for ${plan.name}`}
                aria-haspopup="menu"
            >
                <MoreVertIcon fontSize="small" />
            </IconButton>
            <Menu
                anchorEl={anchor}
                open={!!anchor}
                onClose={close}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <MenuItem onClick={() => { close(); onDelete(plan); }} sx={{ color: DANGER }}>
                    <ListItemIcon><DeleteOutlineIcon fontSize="small" sx={{ color: DANGER }} /></ListItemIcon>
                    <ListItemText primary="Delete plan" />
                </MenuItem>
            </Menu>
        </Box>
    );
};

const GroupHeading = ({ children, first }) => (
    <Typography
        component="h3"
        sx={{ fontSize: '0.95rem', fontWeight: 600, color: INK, mt: first ? 0 : 3, mb: 1.5, pt: first ? 0 : 2.5, borderTop: first ? 'none' : `1px solid ${RULE}` }}
    >
        {children}
    </Typography>
);

// ---------------------------------------------------------------------------
// Create / edit dialog
// ---------------------------------------------------------------------------
const PlanDialog = ({ open, mode, initialValues, saving, apiError, onClose, onSubmit }) => {
    const [form, setForm] = useState(initialValues);
    const [submitted, setSubmitted] = useState(false);

    const errors = validate(form);
    const showError = (field) => submitted && errors[field];
    const currency = CURRENCIES.find((c) => c.value === form.currency);

    const setField = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const setFeature = (key) => (e) => {
        const { checked } = e.target;
        setForm((prev) => ({ ...prev, features: { ...prev.features, [key]: checked } }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setSubmitted(true);
        if (Object.keys(errors).length) return;
        onSubmit({
            name: form.name.trim(),
            price: Number(form.price),
            currency: form.currency,
            messageRateLimit: Number(form.messageRateLimit),
            features: { ...form.features },
            maxUsers: Number(form.maxUsers),
            fileStorageLimit: Number(form.fileStorageLimit),
            isActive: form.isActive,
            description: form.description.trim()
        });
    };

    const numberProps = (field, extra = {}) => ({
        name: field,
        type: 'number',
        fullWidth: true,
        value: form[field],
        onChange: setField,
        error: !!showError(field),
        helperText: showError(field) || extra.helperText || ' ',
        inputProps: { min: 0, step: extra.step || 1, inputMode: extra.step ? 'decimal' : 'numeric' },
        InputProps: extra.InputProps,
        sx: { '& input': { fontVariantNumeric: 'tabular-nums' } }
    });

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
            <Box component="form" noValidate onSubmit={handleSubmit}>
                <DialogTitle sx={{ color: INK, fontWeight: 600 }}>
                    {mode === 'edit' ? `Edit ${initialValues.name || 'plan'}` : 'Create plan'}
                </DialogTitle>
                <DialogContent dividers sx={{ borderColor: RULE }}>
                    {apiError && (
                        <Alert severity="error" variant="outlined" sx={{ mb: 2.5 }}>{apiError}</Alert>
                    )}

                    <GroupHeading first>Name and price</GroupHeading>
                    <TextField
                        name="name"
                        label="Plan name"
                        fullWidth
                        required
                        autoFocus
                        value={form.name}
                        onChange={setField}
                        error={!!showError('name')}
                        helperText={showError('name') || ' '}
                    />
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 2, mt: 1 }}>
                        <TextField
                            label="Price"
                            required
                            {...numberProps('price', {
                                step: '0.01',
                                helperText: 'Per billing month',
                                InputProps: { startAdornment: <InputAdornment position="start">{currency?.symbol}</InputAdornment> }
                            })}
                        />
                        <FormControl fullWidth required>
                            <InputLabel id="plan-currency-label">Currency</InputLabel>
                            <Select
                                labelId="plan-currency-label"
                                name="currency"
                                label="Currency"
                                value={form.currency}
                                onChange={setField}
                            >
                                {CURRENCIES.map((c) => (
                                    <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>

                    <GroupHeading>Limits</GroupHeading>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, columnGap: 2 }}>
                        <TextField label="Message limit" required {...numberProps('messageRateLimit', { helperText: 'Messages allowed' })} />
                        <TextField label="Max users" required {...numberProps('maxUsers', { helperText: 'Team members' })} />
                        <TextField
                            label="File storage"
                            required
                            {...numberProps('fileStorageLimit', {
                                step: '0.5',
                                InputProps: { endAdornment: <InputAdornment position="end">GB</InputAdornment> }
                            })}
                        />
                    </Box>

                    <GroupHeading>Features</GroupHeading>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 2 }}>
                        {FEATURES.map((feature) => (
                            <FormControlLabel
                                key={feature.key}
                                control={<Switch checked={!!form.features[feature.key]} onChange={setFeature(feature.key)} />}
                                label={<Typography variant="body2" sx={{ color: INK }}>{feature.label}</Typography>}
                            />
                        ))}
                    </Box>

                    <GroupHeading>Availability and description</GroupHeading>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={form.isActive}
                                onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
                            />
                        }
                        label={
                            <Box>
                                <Typography variant="body2" sx={{ color: INK }}>Active</Typography>
                                <Typography variant="caption" sx={{ color: MUTED }}>
                                    Customers can only subscribe to active plans.
                                </Typography>
                            </Box>
                        }
                        sx={{ alignItems: 'flex-start', mb: 2, '& .MuiSwitch-root': { mt: -0.5 } }}
                    />
                    <TextField
                        name="description"
                        label="Description"
                        fullWidth
                        multiline
                        minRows={2}
                        value={form.description}
                        onChange={setField}
                        helperText="Shown to customers when they compare plans."
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none' }}>Cancel</Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={saving}
                        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        {mode === 'edit'
                            ? (saving ? 'Saving…' : 'Save changes')
                            : (saving ? 'Creating…' : 'Create plan')}
                    </Button>
                </DialogActions>
            </Box>
        </Dialog>
    );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
const PlanManagement = () => {
    const navigate = useNavigate();
    const { user } = useContext(AuthContext);

    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);

    const [dialog, setDialog] = useState({ open: false, mode: 'create', values: emptyForm, key: 0 });
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [saving, setSaving] = useState(false);
    const [dialogError, setDialogError] = useState(null);

    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    const fetchPlans = useCallback(async () => {
        try {
            setLoading(true);
            setLoadError(null);
            const response = await planService.getPlans({ all: true });
            setPlans(response.data || []);
        } catch (err) {
            setLoadError(err.response?.data?.error || 'Check your connection and try again.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (user && user.role !== 'admin') {
            navigate('/dashboard');
            return;
        }
        fetchPlans();
    }, [user, navigate, fetchPlans]);

    const openCreate = () => {
        setSelectedPlan(null);
        setDialogError(null);
        setDialog((prev) => ({ open: true, mode: 'create', values: emptyForm, key: prev.key + 1 }));
    };

    const openEdit = (plan) => {
        setSelectedPlan(plan);
        setDialogError(null);
        setDialog((prev) => ({
            key: prev.key + 1,
            open: true,
            mode: 'edit',
            values: {
                name: plan.name || '',
                price: String(plan.price ?? 0),
                currency: plan.currency || 'INR',
                messageRateLimit: String(plan.messageRateLimit ?? 0),
                features: {
                    api: plan.features?.api || false,
                    sharedInbox: plan.features?.sharedInbox || false,
                    sentimentAnalysis: plan.features?.sentimentAnalysis || false,
                    bulkMessaging: plan.features?.bulkMessaging || false
                },
                maxUsers: String(plan.maxUsers || 1),
                fileStorageLimit: String(plan.fileStorageLimit || 1),
                isActive: plan.isActive !== false,
                description: plan.description || ''
            }
        }));
    };

    const closeDialog = () => {
        setDialog((prev) => ({ ...prev, open: false }));
        setSelectedPlan(null);
    };

    const handleSave = async (payload) => {
        const isEdit = dialog.mode === 'edit';
        if (isEdit && !selectedPlan) return;
        try {
            setSaving(true);
            setDialogError(null);
            if (isEdit) {
                await planService.updatePlan(selectedPlan._id, payload);
                setSuccess(`${payload.name} updated`);
            } else {
                await planService.createPlan(payload);
                setSuccess(`${payload.name} created`);
            }
            closeDialog();
            fetchPlans();
        } catch (err) {
            setDialogError(err.response?.data?.error || (isEdit ? 'The plan could not be updated.' : 'The plan could not be created.'));
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        try {
            setDeleting(true);
            await planService.deletePlan(deleteTarget._id);
            setSuccess(`${deleteTarget.name} deleted`);
            setDeleteTarget(null);
            fetchPlans();
        } catch (err) {
            setError(err.response?.data?.error || 'The plan could not be deleted.');
        } finally {
            setDeleting(false);
        }
    };

    const activeCount = plans.filter((p) => p.isActive !== false).length;
    const frameSx = { borderColor: RULE, borderRadius: 2, overflow: 'hidden' };

    const renderBody = () => {
        if (loading) {
            return (
                <Paper variant="outlined" sx={frameSx}>
                    <Box sx={{ p: { xs: 2, sm: 3 } }} aria-busy="true">
                        {[0, 1, 2, 3].map((i) => (
                            <Box key={i} sx={{ display: 'flex', gap: 3, py: 1.75, borderTop: i ? `1px solid ${RULE}` : 'none' }}>
                                <Box sx={{ flex: 2 }}><Skeleton width="50%" /><Skeleton width="70%" /></Box>
                                <Box sx={{ flex: 1 }}><Skeleton width="60%" /></Box>
                                <Box sx={{ flex: 2, display: { xs: 'none', md: 'block' } }}><Skeleton width="80%" /></Box>
                                <Box sx={{ flex: 1 }}><Skeleton width="50%" /></Box>
                            </Box>
                        ))}
                    </Box>
                </Paper>
            );
        }

        if (loadError) {
            return (
                <Paper variant="outlined" sx={{ ...frameSx, p: { xs: 3, sm: 5 } }}>
                    <Typography sx={{ fontWeight: 600, color: INK, mb: 1 }}>Plans could not be loaded</Typography>
                    <Typography variant="body2" sx={{ color: MUTED, mb: 2.5, maxWidth: '60ch' }}>{loadError}</Typography>
                    <Button variant="outlined" onClick={fetchPlans} sx={{ textTransform: 'none' }}>Try again</Button>
                </Paper>
            );
        }

        if (!plans.length) {
            return (
                <Paper variant="outlined" sx={{ ...frameSx, p: { xs: 3, sm: 5 } }}>
                    <Typography sx={{ fontWeight: 600, color: INK, mb: 1 }}>No plans yet</Typography>
                    <Typography variant="body2" sx={{ color: MUTED, mb: 2.5, maxWidth: '60ch' }}>
                        Create a plan to set a price, a message limit and the features customers get when they subscribe.
                    </Typography>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={{ textTransform: 'none' }}>
                        Create plan
                    </Button>
                </Paper>
            );
        }

        return (
            <Paper variant="outlined" sx={frameSx}>
                <TableContainer>
                    <Table size="small" sx={{ minWidth: 1040 }}>
                        <TableHead>
                            <TableRow sx={{ '& th': { color: MUTED, fontWeight: 500, borderColor: RULE, py: 1.25, whiteSpace: 'nowrap' } }}>
                                <TableCell sx={{ pl: { xs: 2, sm: 3 } }}>Plan</TableCell>
                                <TableCell align="right">Price</TableCell>
                                <TableCell align="right">Message limit</TableCell>
                                {FEATURES.map((feature) => (
                                    <TableCell key={feature.key} align="center" title={feature.label}>{feature.short}</TableCell>
                                ))}
                                <TableCell align="right">Users</TableCell>
                                <TableCell align="right">Storage</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right" sx={{ pr: 2 }}>
                                    <Box component="span" sx={visuallyHidden}>Actions</Box>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {plans.map((plan) => (
                                <TableRow
                                    key={plan._id}
                                    sx={{
                                        '& td': { borderColor: RULE, py: 1.5, fontVariantNumeric: 'tabular-nums', color: INK },
                                        '&:last-of-type td': { borderBottom: 'none' }
                                    }}
                                >
                                    <TableCell sx={{ pl: { xs: 2, sm: 3 }, maxWidth: 280 }}>
                                        <Typography sx={{ color: INK, fontWeight: 600, lineHeight: 1.35 }}>{plan.name}</Typography>
                                        {plan.description ? (
                                            <Typography variant="body2" sx={{ color: MUTED }} noWrap title={plan.description}>
                                                {plan.description}
                                            </Typography>
                                        ) : (
                                            <Typography variant="body2" sx={{ color: '#B8BFCB' }}>No description</Typography>
                                        )}
                                    </TableCell>
                                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{formatPrice(plan.price, plan.currency)}</TableCell>
                                    <TableCell align="right">{formatCount(plan.messageRateLimit)}</TableCell>
                                    {FEATURES.map((feature) => (
                                        <TableCell key={feature.key} align="center">
                                            <FeatureMark enabled={plan.features?.[feature.key]} label={feature.label} />
                                        </TableCell>
                                    ))}
                                    <TableCell align="right">{plan.maxUsers ?? '–'}</TableCell>
                                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                        {plan.fileStorageLimit != null ? `${plan.fileStorageLimit} GB` : '–'}
                                    </TableCell>
                                    <TableCell><StatusDot active={plan.isActive !== false} /></TableCell>
                                    <TableCell align="right" sx={{ pr: 2 }}>
                                        <RowActions plan={plan} onEdit={openEdit} onDelete={setDeleteTarget} />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        );
    };

    return (
        <AppLayout>
            <Container maxWidth="lg" sx={{ pb: 6 }}>
                {/* Header */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-end', gap: 2, mb: 3 }}>
                    <Box>
                        <Typography component="h1" sx={{ fontSize: { xs: '1.75rem', md: '2.25rem' }, fontWeight: 700, letterSpacing: '-0.025em', color: INK, lineHeight: 1.15 }}>
                            Plans
                        </Typography>
                        <Typography variant="body2" sx={{ color: MUTED, mt: 0.75 }}>
                            {loading || loadError || !plans.length
                                ? 'Set the prices, limits and features customers can subscribe to.'
                                : `${plans.length} ${plans.length === 1 ? 'plan' : 'plans'}, ${activeCount} active. Customers can only subscribe to active plans.`}
                        </Typography>
                    </Box>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={{ textTransform: 'none', fontWeight: 600 }}>
                        Create plan
                    </Button>
                </Box>

                {renderBody()}

                <PlanDialog
                    key={dialog.key}
                    open={dialog.open}
                    mode={dialog.mode}
                    initialValues={dialog.values}
                    saving={saving}
                    apiError={dialogError}
                    onClose={closeDialog}
                    onSubmit={handleSave}
                />

                {/* Delete confirmation */}
                <Dialog open={!!deleteTarget} onClose={deleting ? undefined : () => setDeleteTarget(null)} maxWidth="xs" fullWidth>
                    <DialogTitle sx={{ color: INK, fontWeight: 600 }}>Delete {deleteTarget?.name}?</DialogTitle>
                    <DialogContent>
                        <DialogContentText sx={{ color: MUTED }}>
                            This permanently removes the plan. Customers can no longer choose it, and you can't undo this.
                            To stop offering it while keeping its details, edit the plan and turn off Active instead.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        <Button onClick={() => setDeleteTarget(null)} disabled={deleting} sx={{ textTransform: 'none' }}>Cancel</Button>
                        <Button
                            onClick={handleDelete}
                            variant="contained"
                            color="error"
                            disabled={deleting}
                            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : null}
                            sx={{ textTransform: 'none', fontWeight: 600 }}
                        >
                            {deleting ? 'Deleting…' : 'Delete plan'}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                    <Alert onClose={() => setError(null)} severity="error" variant="filled" sx={{ width: '100%' }}>{error}</Alert>
                </Snackbar>
                <Snackbar open={!!success} autoHideDuration={5000} onClose={() => setSuccess(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                    <Alert onClose={() => setSuccess(null)} severity="success" variant="filled" sx={{ width: '100%' }}>{success}</Alert>
                </Snackbar>
            </Container>
        </AppLayout>
    );
};

const visuallyHidden = {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    whiteSpace: 'nowrap',
    width: 1
};

export default PlanManagement;
